/**
 * Utility functions for managing time dropdowns
 */

export interface TimeOption {
  hour: number;
  minute: number;
  timeString: string;
  durationMinutes?: number;
  durationText?: string;
}

/**
 * Generate standard 24-hour time options in 15-minute increments
 */
export function generateStandardTimeOptions(): TimeOption[] {
  const options: TimeOption[] = [];

  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const formattedHour = hour % 12 || 12; // Convert to 12-hour format
      const period = hour < 12 ? 'am' : 'pm';
      const formattedMinute = minute.toString().padStart(2, '0');
      const timeString = `${formattedHour}:${formattedMinute}${period}`;

      options.push({
        hour,
        minute,
        timeString,
      });
    }
  }

  return options;
}

/**
 * Generate end time options based on start time
 */
export function generateEndTimeOptions(startTimeMinutes: number): TimeOption[] {
  const options: TimeOption[] = [];

  // Track which options to show
  const timeIncrementsToShow = [];

  // Add all 15-minute increments for the first hour
  for (let i = 0; i <= 4; i++) {
    // 0, 15, 30, 45, 60 minutes
    timeIncrementsToShow.push(i);
  }

  // Add 30-minute increments for the rest of the 24 hours
  // But stop at 23.5 hours (index 94) to avoid showing 24 hours (which is a full cycle)
  for (let i = 6; i <= 94; i += 2) {
    // 90, 120, 150... minutes (1.5hr, 2hr, 2.5hr, etc.)
    timeIncrementsToShow.push(i);
  }

  // Generate only the time increments we want to show
  for (const i of timeIncrementsToShow) {
    // Calculate time as start time + i*15 minutes
    const durationMinutes = i * 15;
    const totalMinutes = startTimeMinutes + durationMinutes;
    const currentHour = Math.floor(totalMinutes / 60) % 24;
    const currentMinute = totalMinutes % 60;

    // Format the time
    const formattedHour = currentHour % 12 || 12; // Convert to 12-hour format
    const period = currentHour < 12 ? 'am' : 'pm';
    const formattedMinute = currentMinute.toString().padStart(2, '0');
    const timeString = `${formattedHour}:${formattedMinute}${period}`;

    // Format duration text
    let durationText = '';
    if (durationMinutes === 0) {
      durationText = '(0 mins)';
    } else if (durationMinutes < 60) {
      durationText = `(${durationMinutes} mins)`;
    } else {
      const hours = durationMinutes / 60;
      durationText = `(${Number.isInteger(hours) ? hours : hours.toFixed(1)} hr)`;
    }

    options.push({
      hour: currentHour,
      minute: currentMinute,
      timeString,
      durationMinutes,
      durationText,
    });
  }

  return options;
}

/**
 * Create a time option element
 */
export function createTimeOptionElement(
  option: TimeOption,
  onClick: (option: TimeOption) => void,
  isSelected: boolean = false,
): HTMLDivElement {
  const element = document.createElement('div');
  element.className = 'time-option';
  if (isSelected) {
    element.classList.add('selected');
  }

  if (option.durationText) {
    element.innerHTML = `${option.timeString} <span class="duration">${option.durationText}</span>`;
  } else {
    element.textContent = option.timeString;
  }

  element.dataset.hour = option.hour.toString();
  element.dataset.minute = option.minute.toString();
  element.dataset.period = option.hour < 12 ? 'am' : 'pm';

  if (option.durationMinutes !== undefined) {
    element.dataset.durationMinutes = option.durationMinutes.toString();
  }

  element.addEventListener('click', () => onClick(option));

  return element;
}

/**
 * Populate a dropdown with time options
 */
export function populateDropdown(
  dropdown: HTMLDivElement,
  options: TimeOption[],
  onClick: (option: TimeOption) => void,
  selectedOption?: TimeOption,
): void {
  dropdown.innerHTML = '';
  const fragment = document.createDocumentFragment();
  let selectedElement: HTMLElement | null = null;

  options.forEach(option => {
    const isSelected = selectedOption
      ? selectedOption.hour === option.hour && selectedOption.minute === option.minute
      : option.hour === 9 && option.minute === 0; // Default to 9:00am

    const element = createTimeOptionElement(option, onClick, isSelected);
    fragment.appendChild(element);

    if (isSelected) {
      selectedElement = element;
    }
  });

  dropdown.appendChild(fragment);

  // Scroll to center the selected option in the dropdown
  if (selectedElement) {
    // Use setTimeout to ensure the DOM has been updated
    setTimeout(() => {
      if (selectedElement) {
        // Get the dropdown and selected element dimensions
        const dropdownHeight = dropdown.clientHeight;
        const selectedHeight = selectedElement.clientHeight;

        // Calculate the position to center the selected element
        const selectedTop = selectedElement.offsetTop;
        const centerPosition = selectedTop - dropdownHeight / 2 + selectedHeight / 2;

        // Set the scroll position to center the selected element
        dropdown.scrollTop = Math.max(0, centerPosition);
      }
    }, 0);
  }
}
